import React from 'react';
import { 
  TrendingUp, TrendingDown, AlertTriangle, Package,
  DollarSign, RotateCw, Eye, Star
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';

interface ShelfItem {
  skuId: string;
  skuName: string;
  category: string;
  position: { shelf: number; row: number; column: number };
  facings: number;
  salesPerDay: number;
  profitMargin: number;
  turnoverRate: number;
  status: 'optimal' | 'underperforming' | 'overperforming' | 'dead';
}

interface EfficiencyAnalysisProps {
  shelfItems: ShelfItem[];
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
}

const EfficiencyAnalysis: React.FC<EfficiencyAnalysisProps> = ({
  shelfItems,
  selectedCategory,
  setSelectedCategory
}) => {
  const categories = ['all', '飲料', '菓子', '日配品', '冷凍食品', '生鮮'];
  
  // フィルタリング
  const filteredItems = selectedCategory === 'all' 
    ? shelfItems 
    : shelfItems.filter(item => item.category === selectedCategory);
  
  // 統計計算
  const stats = {
    optimal: filteredItems.filter(i => i.status === 'optimal').length,
    underperforming: filteredItems.filter(i => i.status === 'underperforming').length,
    overperforming: filteredItems.filter(i => i.status === 'overperforming').length,
    dead: filteredItems.filter(i => i.status === 'dead').length,
    avgSales: filteredItems.reduce((sum, i) => sum + i.salesPerDay, 0) / filteredItems.length,
    avgProfit: filteredItems.reduce((sum, i) => sum + i.profitMargin, 0) / filteredItems.length,
    avgTurnover: filteredItems.reduce((sum, i) => sum + i.turnoverRate, 0) / filteredItems.length,
  };
  
  // ゾーン別分析（アイレベル、ゴールデンゾーンなど）
  const zoneAnalysis = [
    {
      zone: 'アイレベル',
      shelves: [2, 3],
      efficiency: 85,
      sales: 450000,
      description: '最も視認性が高く売上貢献度大'
    },
    {
      zone: 'ゴールデンゾーン',
      shelves: [2],
      efficiency: 92,
      sales: 380000,
      description: '手に取りやすい高効率ゾーン'
    },
    {
      zone: '上段',
      shelves: [1],
      efficiency: 65,
      sales: 250000,
      description: 'ストック商品・大型商品向け'
    },
    {
      zone: '下段',
      shelves: [4, 5],
      efficiency: 58,
      sales: 180000,
      description: '重量商品・廉価商品向け'
    }
  ];
  
  // トップ/ワースト商品
  const topPerformers = [...filteredItems]
    .sort((a, b) => b.salesPerDay - a.salesPerDay)
    .slice(0, 5);
  
  const worstPerformers = [...filteredItems]
    .filter(i => i.status === 'dead' || i.status === 'underperforming')
    .sort((a, b) => a.turnoverRate - b.turnoverRate)
    .slice(0, 5);

  // ヒートマップデータ生成
  const generateHeatmapData = () => {
    const heatmap: number[][] = [];
    for (let shelf = 1; shelf <= 5; shelf++) {
      const row: number[] = [];
      for (let col = 1; col <= 8; col++) {
        const item = filteredItems.find(
          i => i.position.shelf === shelf && i.position.column === col && i.position.row === 2
        );
        row.push(item ? item.salesPerDay : 0);
      }
      heatmap.push(row);
    }
    return heatmap;
  };
  
  const heatmapData = generateHeatmapData();
  const maxSales = Math.max(...heatmapData.flat());

  return (
    <div className="space-y-6">
      {/* カテゴリフィルター */}
      <div className="flex items-center gap-2">
        {categories.map(cat => (
          <Button
            key={cat}
            variant={selectedCategory === cat ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setSelectedCategory(cat)}
          >
            {cat === 'all' ? '全カテゴリ' : cat}
          </Button>
        ))}
      </div>

      {/* 効率サマリー */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Star size={20} style={{ color: colors.support.success }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {stats.optimal}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                最適配置
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <TrendingDown size={20} style={{ color: colors.status.warning }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {stats.underperforming}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                改善必要
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} style={{ color: colors.status.error }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {stats.dead}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                死筋商品
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <RotateCw size={20} style={{ color: colors.accent.blue }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {stats.avgTurnover.toFixed(1)}回
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                平均回転率
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 売上ヒートマップ */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
          売上効率ヒートマップ
        </h3>
        
        <div className="overflow-x-auto">
          <div className="inline-block">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs" style={{ color: colors.text.secondary }}>低</span>
              <div className="flex h-4">
                {[0, 0.2, 0.4, 0.6, 0.8, 1].map((val, idx) => (
                  <div
                    key={idx}
                    className="w-8 h-full"
                    style={{
                      backgroundColor: `rgba(239, 68, 68, ${val})`,
                    }}
                  />
                ))}
              </div>
              <span className="text-xs" style={{ color: colors.text.secondary }}>高</span>
            </div>
            
            <div className="relative">
              {/* 棚段ラベル */}
              <div className="absolute -left-12 top-0 h-full flex flex-col justify-around">
                {['上段', 'アイ上', 'アイレベル', 'アイ下', '下段'].map((label, idx) => (
                  <span key={idx} className="text-xs" style={{ color: colors.text.secondary }}>
                    {label}
                  </span>
                ))}
              </div>
              
              {/* ヒートマップグリッド */}
              <div className="space-y-1">
                {heatmapData.map((row, shelfIdx) => (
                  <div key={shelfIdx} className="flex gap-1">
                    {row.map((sales, colIdx) => (
                      <div
                        key={colIdx}
                        className="w-12 h-12 rounded flex items-center justify-center text-xs font-medium cursor-pointer hover:ring-2"
                        style={{
                          backgroundColor: sales > 0 
                            ? `rgba(239, 68, 68, ${sales / maxSales})`
                            : colors.ui.bgSecondary,
                          color: sales / maxSales > 0.5 ? 'white' : colors.text.primary
                        }}
                        title={`売上: ¥${Math.round(sales)}`}
                      >
                        {sales > 0 ? Math.round(sales / 10) : '-'}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              
              {/* 列番号 */}
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(col => (
                  <div key={col} className="w-12 text-center text-xs" style={{ color: colors.text.secondary }}>
                    {col}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        {/* ゾーン別分析 */}
        <Card className="p-4">
          <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
            ゾーン別効率分析
          </h3>
          
          <div className="space-y-3">
            {zoneAnalysis.map((zone, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium" style={{ color: colors.text.primary }}>
                      {zone.zone}
                    </span>
                    <span className="ml-2 text-xs" style={{ color: colors.text.secondary }}>
                      (棚{zone.shelves.join(', ')})
                    </span>
                  </div>
                  <Badge 
                    variant={zone.efficiency > 80 ? 'success' : zone.efficiency > 60 ? 'warning' : 'error'}
                  >
                    効率 {zone.efficiency}%
                  </Badge>
                </div>
                
                <div className="text-xs" style={{ color: colors.text.secondary }}>
                  {zone.description}
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: colors.text.secondary }}>売上貢献</span>
                  <span className="font-medium">¥{(zone.sales / 1000).toFixed(0)}K</span>
                </div>
                
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full transition-all"
                    style={{ 
                      width: `${zone.efficiency}%`,
                      backgroundColor: zone.efficiency > 80 ? colors.support.success : 
                                     zone.efficiency > 60 ? colors.status.warning : 
                                     colors.status.error
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* パフォーマンスランキング */}
        <Card className="p-4">
          <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
            パフォーマンスランキング
          </h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: colors.support.success }}>
                <TrendingUp size={16} />
                トップパフォーマー
              </h4>
              <div className="space-y-2">
                {topPerformers.map((item, idx) => (
                  <div key={item.skuId} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-bold" style={{ color: colors.accent.blue }}>
                        #{idx + 1}
                      </span>
                      <span>{item.skuName}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs">¥{Math.round(item.salesPerDay)}/日</span>
                      <Badge variant="success" size="small">
                        {item.turnoverRate.toFixed(1)}回転
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: colors.status.error }}>
                <TrendingDown size={16} />
                要改善商品
              </h4>
              <div className="space-y-2">
                {worstPerformers.map((item, idx) => (
                  <div key={item.skuId} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={14} style={{ color: colors.status.warning }} />
                      <span>{item.skuName}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs">¥{Math.round(item.salesPerDay)}/日</span>
                      <Badge variant="error" size="small">
                        {item.turnoverRate.toFixed(1)}回転
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EfficiencyAnalysis;
